import {
  CompanyPickerDialog,
  CompanySwitcher,
  EmptyCard,
  Loader,
  TryAgain,
} from "@/components";
import useDomainSelector from "@/hooks/useDomainSelector";
import { getCompanyKey } from "@/core/utils/helpers";
import { FC, PropsWithChildren } from "react";
import { useTranslation } from "react-i18next";
import welcomeImage from "assets/images/png/welcome.png";
import type { DomainSelectorProps } from "@/guard/DomainSelector/DomainSelector.types";

const DomainSelector: FC<PropsWithChildren<DomainSelectorProps>> = ({
  children,
  tokenReady,
  requiresBrowserAuth,
  onRequestBrowserAuth,
}) => {
  const { t } = useTranslation();
  const {
    companyId,
    companyApiDomain,
    selectCompany,
    loading,
    consentRequired,
    requestConsent,
    companies,
    error,
    noWorkspace,
    authExpired,
    retrySignIn,
    retry,
    goToMarketingSite,
    goToHelpdesk,
  } = useDomainSelector(tokenReady);

  if (loading) return <Loader label={t("Finding your workspace...")} />;

  if (requiresBrowserAuth) {
    return (
      <EmptyCard
        img={welcomeImage}
        title={t("Sign in required")}
        link={{
          title: t("Click here to sign in"),
          onClick: onRequestBrowserAuth,
        }}
      />
    );
  }

  if (authExpired) {
    return (
      <EmptyCard
        img={welcomeImage}
        title={t("Sign in required")}
        link={{
          title: t("Click here to sign in"),
          onClick: retrySignIn,
        }}
      />
    );
  }

  if (consentRequired) {
    return (
      <EmptyCard
        img={welcomeImage}
        title={t("Permission needed")}
        description={t(
          'Rise Up needs your permission to sign you in with your Microsoft account. If Microsoft says an administrator must approve it, ask your IT team to approve "Rise Up for Teams".',
        )}
        link={{
          title: t("Allow access"),
          onClick: requestConsent,
        }}
      />
    );
  }

  if (error) {
    return (
      <TryAgain
        onClick={retry}
        title={t("Something went wrong")}
        description={error}
        buttonTitle={t("Try again")}
      />
    );
  }

  if (companyId) {
    return (
      <>
        {children}
        {companies.length > 1 && (
          <CompanySwitcher
            companies={companies}
            currentCompanyKey={getCompanyKey({
              apiDomain: companyApiDomain,
              id: companyId,
            })}
            onSwitch={selectCompany}
          />
        )}
      </>
    );
  }

  if (noWorkspace) {
    return (
      <EmptyCard
        img={welcomeImage}
        title={t("Welcome to Rise Up")}
        description={t(
          "To get started, an active Rise Up account is required. Please contact your administrator to set up your access.",
        )}
        primaryButton={{
          title: t("Get started with Rise Up"),
          onClick: goToMarketingSite,
        }}
        link={{
          title: t("Already have an account? Contact Helpdesk"),
          onClick: goToHelpdesk,
        }}
      />
    );
  }

  if (companies.length <= 1) {
    return <Loader label={t("Finding your workspace...")} />;
  }

  return (
    <CompanyPickerDialog companies={companies} onValidate={selectCompany} />
  );
};

export default DomainSelector;
